import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useRecoilState } from "recoil";
import useAuth from "../../hooks/useAuth";
import { useNotification } from "../../hooks/useNotification";
import { useZoomSettings } from "../../hooks/useZoomSettings";
import { notificationModalOpenState } from "../../recoil/notification";
import Dashboard from "./tabs/dashboard/Dashboard";
import MyAdvertisement from "./tabs/myAdvertisement/MyAdvertisement";
import Settings from "./tabs/Settings";
import Deposit from "./tabs/deposit/Deposit";
import AddAdvertisement from "./tabs/addAdvertisement/AddAdvertisement";
import CheckerManagement from "./tabs/checkerManagement/CheckerManagement";
import RankCheck from "./tabs/rankKeyword/RankCheck";
import KeywordAnalysis from "./tabs/rankKeyword/KeywordAnalysis";
import NotificationModal from "../../components/NotificationModal";
import FAQModal from "../../components/FAQModal";
import NoticeIsland from "../../components/NoticeIsland/NoticeIsland";
import GuidePage from "../guide/GuidePage";
import GuideArticlePage from "../guide/GuideArticlePage";
import { getEnvironmentInfo } from "../../utils/environment";
import {
  MdDashboard,
  MdOutlineAdsClick,
  MdMiscellaneousServices,
  MdAccountBalanceWallet,
  MdBarChart,
  MdSearch,
} from "react-icons/md";
import { IoBookOutline, IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { BsQuestionCircle } from "react-icons/bs";
import bellIcon from "../../assets/images/bell.png";
import logoIcon from "../../assets/images/logo.png";
import dashboardIcon from "../../assets/icons/deshboard.svg";
import dashboardIconActive from "../../assets/icons/deshboardActive.svg";
import advertismentIcon from "../../assets/icons/advertismentIcon.svg";
import advertismentIconActive from "../../assets/icons/advertismentIconActive.svg";
import checkAdvertisementIcon from "../../assets/icons/checkAdvertisement.svg";
import checkAdvertisementIconActive from "../../assets/icons/checkAdvertisementActive.svg";
import balanceIcon from "../../assets/icons/balance.svg";
import balanceIconActive from "../../assets/icons/balanceActive.svg";
import bookIcon from "../../assets/icons/book.svg";
import infoIcon from "../../assets/icons/info.svg";
import kakaotalkIcon from "../../assets/icons/kakaotalk.svg";
import HomeMobile from "./HomeMobile";
import "./Home.css";

/** price_grade.name → 뱃지 표기/톤 (TOP1→TOP, 미매칭→원문+TOP UI) */
function resolvePriceGradeBadge(gradeName) {
  const raw = String(gradeName || "").trim();
  if (!raw) return { label: "", tone: "top" };
  const upper = raw.toUpperCase();
  if (upper.includes("VVIP")) return { label: "VVIP", tone: "vvip" };
  if (upper.includes("VIP")) return { label: "VIP", tone: "vip" };
  if (upper.includes("BASIC")) return { label: "BASIC", tone: "basic" };
  if (upper.includes("TOP")) return { label: "TOP", tone: "top" };
  return { label: raw, tone: "top" };
}
const Home = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("myAdvertisement");
  const [depositSubTab, setDepositSubTab] = useState("chargeRequest");
  const [tempDepositSubTab, setTempDepositSubTab] = useState(null);
  const [hoveredTab, setHoveredTab] = useState(null);

  // 모바일 감지
  const [isMobile, setIsMobile] = useState(false);

  // 알림 관련
  const { unreadCount, fetchNotifications } = useNotification();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useRecoilState(
    notificationModalOpenState,
  );
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  // Zoom Settings 관련
  const { settings } = useZoomSettings();

  React.useEffect(() => {
    // 초기 모바일 감지 (한 번만, 더 정확한 방법 사용)
    const initialWidth = window.visualViewport?.width || window.innerWidth;
    const initialIsMobile = initialWidth <= 768;
    setIsMobile(initialIsMobile);

    // 모바일로 감지되면 리사이즈 이벤트 무시 (옛날 디자인으로 넘어가는 것 방지)
    if (initialIsMobile) {
      return;
    }

    // 데스크톱인 경우에만 리사이즈 이벤트 처리
    const checkMobile = () => {
      const width = window.visualViewport?.width || window.innerWidth;
      setIsMobile(width <= 768);
    };

    // 디바운싱하여 너무 자주 발생하지 않도록
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 100);
    };

    window.addEventListener("resize", handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
      clearTimeout(resizeTimeout);
    };
  }, []);

  // SEO 메타 데이터 생성
  const getSEOMetaData = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const subTab = params.get("subTab");
    const mainTab = params.get("mainTab");

    let title = "올려줌 - 순위 상위 노출 셀프 마케팅";
    let description =
      "인스타그램, 유튜브, 틱톡, 스레드, 스마트스토어, 네이버플레이스까지 검색·방문·리뷰·저장·노출을 실제 유저가 직접 실행하는 리워드 트래픽 기반 마케팅 셀프마케팅의 해답, 셀러 대행사 중심 실행 플랫폼 올려줌.";
    let canonicalUrl = "https://www.olzoom.com/";
    let keywords =
      "올려줌,olzoom,셀프마케팅,리워드마케팅,인스타그램마케팅,유튜브마케팅,틱톡마케팅,네이버플레이스,스마트스토어,마케팅플랫폼";
    let pageType = "home";

    if (tab) {
      canonicalUrl += `?tab=${tab}`;

      switch (tab) {
        case "dashboard":
          title = "대시보드 - 올려줌 | 광고 현황 및 통계";
          description =
            "올려줌 대시보드에서 광고 현황과 통계를 한눈에 확인하세요. 실시간 광고 성과, 예치금 현황, 키워드 순위 변화를 모니터링할 수 있습니다.";
          keywords =
            "광고대시보드, 마케팅통계, 광고성과, 키워드순위, 예치금현황";
          pageType = "dashboard";
          break;
        case "addAdvertisement":
          title = "광고 신청 - 올려줌 | 네이버 쇼핑/플레이스/앱리뷰 마케팅";
          description =
            "네이버 쇼핑, 플레이스, 앱 리뷰 등 다양한 마케팅 서비스를 신청하세요. 셀프마케팅으로 실제 유저의 검색, 방문, 리뷰, 저장을 확보할 수 있습니다.";
          keywords =
            "광고신청, 네이버쇼핑광고, 네이버플레이스광고, 앱리뷰광고, 셀프마케팅신청";
          pageType = "addAdvertisement";
          break;
        case "myAdvertisement":
          title = "내 광고 관리 - 올려줌 | 광고 현황 및 성과 관리";
          description =
            "신청한 광고의 진행 상황과 성과를 실시간으로 확인하고 관리하세요. 광고 상태, 완료율, 키워드 순위 변화를 한눈에 볼 수 있습니다.";
          keywords = "광고관리, 광고현황, 광고성과, 키워드순위관리, 마케팅관리";
          pageType = "myAdvertisement";
          break;
        case "deposit":
          title = "예치금 관리 - 올려줌 | 충전/환불/이용내역";
          description =
            "예치금 충전, 환불, 이용내역을 편리하게 관리하세요. 실시간 잔액 확인과 상세한 거래 내역을 제공합니다.";
          keywords = "예치금관리, 예치금충전, 예치금환불, 이용내역, 잔액확인";
          pageType = "deposit";
          break;
        case "settings":
          title = "설정 - 올려줌 | 계정 및 개인정보 관리";
          description =
            "올려줌 계정 설정과 개인정보를 관리하세요. 프로필 수정, 알림 설정, 보안 설정을 변경할 수 있습니다.";
          keywords = "계정설정, 개인정보관리, 프로필수정, 알림설정, 보안설정";
          pageType = "settings";
          break;
        case "rankCheck":
          title = "올려줌 순위 체크 | 스마트스토어 상품·키워드 광고 순위";
          description =
            "올려줌(olzoom.com 공식)에서 스마트스토어 간편 상품·키워드 광고 순위를 확인하고 관리하세요. 셀프 마케팅 플랫폼.";
          keywords =
            "올려줌,olzoom,스마트스토어순위,네이버광고순위,순위체크,쇼핑순위";
          pageType = "rankCheck";
          break;
        case "keywordAnalysis":
          title = "올려줌 키워드 분석 | 네이버 연관 키워드·검색량";
          description =
            "올려줌(olzoom.com 공식)에서 네이버 연관 키워드 검색량·경쟁도·블로그 포화도를 분석하세요. 셀프 마케팅 플랫폼.";
          keywords =
            "올려줌,olzoom,연관키워드,키워드분석,검색량,네이버키워드";
          pageType = "keywordAnalysis";
          break;
        case "rankKeyword":
          title = "올려줌 순위 체크 | 스마트스토어 상품·키워드 광고 순위";
          description =
            "올려줌(olzoom.com 공식)에서 스마트스토어 간편 상품·키워드 광고 순위를 확인하고 관리하세요. 셀프 마케팅 플랫폼.";
          keywords =
            "올려줌,olzoom,스마트스토어순위,네이버광고순위,순위체크,쇼핑순위";
          pageType = "rankCheck";
          break;
      }

      // 서브탭별 상세 정보 추가
      if (subTab) {
        canonicalUrl += `&subTab=${subTab}`;

        switch (subTab) {
          case "chargeRequest":
            title += " | 충전신청";
            description += " 예치금 충전을 신청하고 결제를 진행하세요.";
            break;
          case "chargeHistory":
            title += " | 충전내역";
            description += " 과거 충전 내역을 확인하세요.";
            break;
          case "refundRequest":
            title += " | 환불신청";
            description += " 예치금 환불을 신청하세요.";
            break;
          case "refundHistory":
            title += " | 환불내역";
            description += " 과거 환불 내역을 확인하세요.";
            break;
          case "usageHistory":
            title += " | 이용내역";
            description += " 광고 서비스 이용 내역을 확인하세요.";
            break;
        }
      }

      // 메인탭별 상세 정보 추가
      if (mainTab) {
        canonicalUrl += `&mainTab=${mainTab}`;

        switch (mainTab) {
          case "naver":
            title += " | 네이버 마케팅";
            description += " 네이버 쇼핑, 플레이스, 저장 서비스를 관리하세요.";
            break;
          case "review":
            title += " | 앱 리뷰";
            description += " 앱스토어 리뷰 마케팅을 관리하세요.";
            break;
        }
      }
    }

    return { title, description, canonicalUrl, keywords, pageType };
  };

  const { title, description, canonicalUrl, keywords, pageType } =
    getSEOMetaData();

  // URL 파라미터에서 원하는 탭 확인. 가이드는 /guide 고정 경로라 탭 쿼리로 바꾸지 않는다.
  useEffect(() => {
    if (location.pathname.startsWith("/guide")) return;

    const params = new URLSearchParams(location.search);
    const desiredTab = params.get("tab");
    const desiredSubTab = params.get("subTab");

    if (
      desiredTab &&
      [
        "dashboard",
        "users",
        "settings",
        "deposit",
        "addAdvertisement",
        "myAdvertisement",
        "checkerManagement",
        "rankCheck",
        "keywordAnalysis",
        "rankKeyword",
      ].includes(desiredTab)
    ) {
      // 대시보드 탭은 비노출 — URL 진입 시 광고 확인으로 리다이렉트
      if (desiredTab === "dashboard") {
        setActiveTab("myAdvertisement");
        navigate("/?tab=myAdvertisement", { replace: true });
        return;
      }

      // 구 통합 탭 → 순위 체크로 리다이렉트
      if (desiredTab === "rankKeyword") {
        setActiveTab("rankCheck");
        navigate("/?tab=rankCheck", { replace: true });
        return;
      }

      // CHECKER 역할은 광고 등록, 예치금 관리 접근 불가
      if (user?.role === "CHECKER") {
        if (
          desiredTab === "addAdvertisement" ||
          desiredTab === "deposit"
        ) {
          setActiveTab("myAdvertisement");
          navigate("/?tab=myAdvertisement", { replace: true });
          return;
        }
      }

      // 체커 관리는 AGENCY만 접근
      if (desiredTab === "checkerManagement" && user?.role !== "AGENCY") {
        setActiveTab("myAdvertisement");
        navigate("/?tab=myAdvertisement", { replace: true });
        return;
      }

      setActiveTab(desiredTab);

      // 서브탭이 있는 경우 처리
      if (desiredSubTab && desiredTab === "deposit") {
        setTempDepositSubTab(desiredSubTab);
      }
    } else {
      // URL 파라미터가 없으면 광고 확인이 기본
      setActiveTab("myAdvertisement");
      navigate("/?tab=myAdvertisement", { replace: true });
    }
  }, [location.pathname, location.search, navigate, user?.role]);

  const isGuideRoute = location.pathname.startsWith("/guide");
  const isGuideArticle = /^\/guide\/.+/.test(location.pathname);

  // 모바일이면 모바일 컴포넌트 렌더링
  if (isMobile) {
    return <HomeMobile />;
  }

  const handleLogout = async () => {
    // logout() 함수가 이미 window.location.replace로 리다이렉트하므로
    // navigate는 호출하지 않음 (중복 리다이렉트 방지)
    await logout();
    navigate("/login");
  };

  const handleFAQClick = () => {
    setIsFAQModalOpen(true);
  };

  const handleKakaoChat = () => {
    if (settings?.kakaoChannelId) {
      // 카카오톡 채널 링크로 이동
      window.open(settings.kakaoChannelId, "_blank");
    } else {
      // 기본 카카오톡 링크 또는 알림
      alert("카카오톡 상담 링크를 준비 중입니다.");
    }
  };

  const handleNotificationClick = async () => {
    await fetchNotifications(1);
    setIsNotificationModalOpen(true);
  };

  const handleTabClick = (tabName, subTab = null, statusFilter = null) => {
    // 대시보드 탭은 비노출 — 클릭/호출 시 광고 확인으로 이동
    if (tabName === "dashboard") {
      setActiveTab("myAdvertisement");
      navigate("/?tab=myAdvertisement", { replace: true });
      return;
    }

    // CHECKER 역할은 광고 등록, 예치금 관리 접근 불가
    if (user?.role === "CHECKER") {
      if (tabName === "addAdvertisement" || tabName === "deposit") {
        return;
      }
    }

    // 예치금 관리 탭으로 이동하면서 서브탭 지정
    if (tabName === "deposit" && subTab) {
      setTempDepositSubTab(subTab);
    } else {
      // 다른 탭으로 이동할 때는 임시 서브탭 초기화
      setTempDepositSubTab(null);
    }

    /// TODO:
    // // 다른 탭들은 로그인 필요
    // if (!user) {
    //   // 로그인 페이지로 이동하면서 원하는 탭 정보를 URL 파라미터로 전달
    //   navigate(`/login?redirect=home&tab=${tabName}`);
    //   return;
    // }

    setActiveTab(tabName);

    // URL 업데이트 (등록 완료 → 내 광고 확인 시에만 status=PENDING)
    const statusQuery =
      tabName === "myAdvertisement" && statusFilter
        ? `&status=${statusFilter}`
        : "";
    navigate(
      `/?tab=${tabName}${subTab ? `&subTab=${subTab}` : ""}${statusQuery}`,
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard onTabChange={handleTabClick} />;
      /// TODO:
      // case "users":
      //   return user ? (
      //     <Users />
      //   ) : (
      //     <div className="login-required">로그인이 필요한 페이지입니다.</div>
      //   );
      // case "settings":
      //   return user ? (
      //     <Settings />
      //   ) : (
      //     <div className="login-required">로그인이 필요한 페이지입니다.</div>
      //   );
      // case "deposit":
      //   return user ? (
      //     <Deposit />
      //   ) : (
      //     <div className="login-required">로그인이 필요한 페이지입니다.</div>
      //   );
      case "addAdvertisement":
        return <AddAdvertisement onTabChange={handleTabClick} />;
      case "myAdvertisement":
        return <MyAdvertisement />;
      case "settings":
        return <Settings />;
      case "deposit":
        return <Deposit initialTab={tempDepositSubTab || depositSubTab} />;
      case "checkerManagement":
        return <CheckerManagement />;
      case "rankCheck":
        return <RankCheck />;
      case "keywordAnalysis":
        return <KeywordAnalysis />;
      case "rankKeyword":
        return <RankCheck />;
      default:
        return <MyAdvertisement />;
    }
  };

  const gradeBadge = user?.price_grade?.name
    ? resolvePriceGradeBadge(user.price_grade.name)
    : null;

  return (
    <div className="home-container">
      {!isGuideRoute && (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow" />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="올려줌" />
        <meta property="og:locale" content="ko_KR" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />

        {/* 구조화된 데이터 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            description: description,
            url: canonicalUrl,
            mainEntity: {
              "@type": "WebSite",
              name: "올려줌",
              description: "셀프 마케팅 플랫폼",
              url: "https://www.olzoom.com/",
              potentialAction: {
                "@type": "SearchAction",
                target: "https://www.olzoom.com/?tab=addAdvertisement",
                "query-input": "required name=search_term_string",
              },
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "홈",
                  item: "https://www.olzoom.com/",
                },
                ...(pageType !== "home"
                  ? [
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: title.split(" - ")[0],
                        item: canonicalUrl,
                      },
                    ]
                  : []),
              ],
            },
            ...(pageType !== "home" && {
              mainEntity: {
                "@type":
                  pageType === "addAdvertisement"
                    ? "Service"
                    : "WebApplication",
                name: title,
                description: description,
                url: canonicalUrl,
                ...(pageType === "addAdvertisement" && {
                  serviceType: "Digital Marketing",
                  provider: {
                    "@type": "Organization",
                    name: "올려줌",
                  },
                  areaServed: "KR",
                }),
                ...(pageType !== "addAdvertisement" && {
                  applicationCategory:
                    pageType === "deposit"
                      ? "FinanceApplication"
                      : "BusinessApplication",
                  operatingSystem: "Web Browser",
                }),
              },
            }),
          })}
        </script>
      </Helmet>
      )}
      <div className="home-content">
        <div className="home-sidebar">
          {/* 사이드바 헤더 */}
          <div className="home-sidebar-header">
            <div className="home-sidebar-logo">
              <img src={logoIcon} alt="올려줌" className="home-logo-img" />
            </div>
            {user && (
              <div className="home-sidebar-user-card">
                <div className="home-sidebar-user-info">
                  {gradeBadge && (
                    <span className={`home-grade-tag is-${gradeBadge.tone}`}>
                      {gradeBadge.label}
                    </span>
                  )}
                  <span className="home-user-name">{user.name} 님</span>
                  <button
                    className="home-notification-btn"
                    onClick={handleNotificationClick}
                    type="button"
                    aria-label="알림"
                  >
                    <img
                      src={bellIcon}
                      alt="알림"
                      className="home-notification-icon"
                    />
                    {unreadCount > 0 && (
                      <span className="notification-badge">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>
                </div>
                {user?.deposit !== undefined && (
                  <div className="home-deposit-info">
                    <span className="home-deposit-label">예치금</span>
                    <strong>{user.deposit.toLocaleString()}원</strong>
                  </div>
                )}
              </div>
            )}
          </div>
          <nav className="home-tab-nav">
            {/* 기존 dashboard 탭(위젯 홈) 일시 비노출 — URL 진입은 myAdvertisement로 리다이렉트 */}
            <button
              className={`home-tab-btn ${
                activeTab === "myAdvertisement" ? "active" : ""
              }`}
              onClick={() => handleTabClick("myAdvertisement")}
              onMouseEnter={() => setHoveredTab("myAdvertisement")}
              onMouseLeave={() => setHoveredTab(null)}
            >
              <img
                src={
                  activeTab === "myAdvertisement" ||
                  hoveredTab === "myAdvertisement"
                    ? dashboardIconActive
                    : dashboardIcon
                }
                alt="대시보드"
                className="home-tab-icon"
              />
              <span>대시보드</span>
            </button>
            <button
              className={`home-tab-btn ${
                activeTab === "addAdvertisement" ? "active" : ""
              } ${!user || user?.role === "CHECKER" ? "disabled" : ""}`}
              onClick={() => handleTabClick("addAdvertisement")}
              onMouseEnter={() =>
                !user ||
                user?.role === "CHECKER" ||
                setHoveredTab("addAdvertisement")
              }
              onMouseLeave={() => setHoveredTab(null)}
              disabled={!user || user?.role === "CHECKER"}
            >
              <img
                src={
                  activeTab === "addAdvertisement" ||
                  hoveredTab === "addAdvertisement"
                    ? advertismentIconActive
                    : advertismentIcon
                }
                alt="광고 등록"
                className="home-tab-icon"
              />
              <span>광고 등록</span>
            </button>
            <button
              className={`home-tab-btn ${
                activeTab === "deposit" ? "active" : ""
              } ${!user || user?.role === "CHECKER" ? "disabled" : ""}`}
              onClick={() => handleTabClick("deposit")}
              onMouseEnter={() =>
                !user || user?.role === "CHECKER" || setHoveredTab("deposit")
              }
              onMouseLeave={() => setHoveredTab(null)}
              disabled={!user || user?.role === "CHECKER"}
            >
              <img
                src={
                  activeTab === "deposit" || hoveredTab === "deposit"
                    ? balanceIconActive
                    : balanceIcon
                }
                alt="예치금 관리"
                className="home-tab-icon"
              />
              <span>예치금 관리</span>
            </button>
            {user?.role === "AGENCY" && (
              <button
                className={`home-tab-btn ${
                  activeTab === "checkerManagement" ? "active" : ""
                }`}
                onClick={() => handleTabClick("checkerManagement")}
                onMouseEnter={() => setHoveredTab("checkerManagement")}
                onMouseLeave={() => setHoveredTab(null)}
              >
                <img
                  src={
                    activeTab === "checkerManagement" ||
                    hoveredTab === "checkerManagement"
                      ? checkAdvertisementIconActive
                      : checkAdvertisementIcon
                  }
                  alt="체커 관리"
                  className="home-tab-icon"
                />
                <span>체커 관리</span>
              </button>
            )}
            <button
              type="button"
              className={`home-tab-btn ${
                activeTab === "rankCheck" ? "active" : ""
              }`}
              onClick={() => handleTabClick("rankCheck")}
              onMouseEnter={() => setHoveredTab("rankCheck")}
              onMouseLeave={() => setHoveredTab(null)}
            >
              <MdBarChart className="home-tab-icon" />
              <span>순위 체크</span>
            </button>
            <button
              type="button"
              className={`home-tab-btn ${
                activeTab === "keywordAnalysis" ? "active" : ""
              }`}
              onClick={() => handleTabClick("keywordAnalysis")}
              onMouseEnter={() => setHoveredTab("keywordAnalysis")}
              onMouseLeave={() => setHoveredTab(null)}
            >
              <MdSearch className="home-tab-icon" />
              <span>키워드 분석</span>
            </button>
          </nav>
          <div className="home-sidebar-footer">
            {/* 노션 이용 가이드
            window.open(
              "https://www.notion.so/2976cc616fe380a28d1bc637615607ec?source=copy_link",
              "_blank",
            );
            */}
            {/* 마케팅 게시글 — 사이드바 진입 비활성
            <Link
              to="/guide"
              className={`home-footer-btn${isGuideRoute ? " is-active" : ""}`}
            >
              <div
                className="home-footer-icon-wrapper"
                style={{ "--icon-url": `url(${bookIcon})` }}
              >
                <img
                  src={bookIcon}
                  alt="마케팅 게시글"
                  className="home-footer-icon"
                />
              </div>
              <span>마케팅 게시글</span>
            </Link>
            */}
            <button className="home-footer-btn" onClick={handleFAQClick}>
              <div
                className="home-footer-icon-wrapper"
                style={{ "--icon-url": `url(${infoIcon})` }}
              >
                <img
                  src={infoIcon}
                  alt="자주묻는질문"
                  className="home-footer-icon"
                />
              </div>
              <span>자주묻는질문</span>
            </button>
            <button className="home-footer-btn" onClick={handleKakaoChat}>
              <div
                className="home-footer-icon-wrapper"
                style={{ "--icon-url": `url(${kakaotalkIcon})` }}
              >
                <img
                  src={kakaotalkIcon}
                  alt="카카오톡 상담하기"
                  className="home-footer-icon"
                />
              </div>
              <span>카카오톡 상담하기</span>
            </button>
            {user && (
              <button
                onClick={handleLogout}
                className="home-logout-btn-sidebar"
              >
                로그아웃
              </button>
            )}
            {!user && (
              <button
                onClick={() => navigate("/login")}
                className="home-login-btn-sidebar"
              >
                로그인
              </button>
            )}
          </div>
        </div>

        <div className="home-main-content">
          {isGuideRoute ? (
            isGuideArticle ? (
              <GuideArticlePage />
            ) : (
              <GuidePage />
            )
          ) : (
            <>
              <NoticeIsland />
              {renderTabContent()}
            </>
          )}
        </div>
      </div>

      {/* 알림 모달 */}
      <NotificationModal />

      {/* FAQ 모달 */}
      <FAQModal
        isOpen={isFAQModalOpen}
        onClose={() => setIsFAQModalOpen(false)}
      />
    </div>
  );
};

export default Home;
